import { Component, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { MainLayoutComponent } from './layout/main-layout.component';
import { VolunteerLayoutComponent } from './layout/volunteer-layout.component';
import { NgoLayoutComponent } from './layout/ngo-layout.component';
import { NgIf } from '@angular/common';
import { ToasterStackComponent } from './shared/common-components/toaster/toaster-stack.component';
import { LoadingSpinnerComponent } from './shared/common-components/loading-spinner/loading-spinner.component';
import { ConfirmationModalComponent } from './shared/common-components/confirmation-modal/confirmation-modal.component';
import { PaymentStatusModalComponent } from './shared/common-components/payment-status-modal/payment-status-modal.component';
import { LogoutConfirmationService } from './shared/services/logout-confirmation.service';
import { AuthService } from './shared/services/auth.service';
import { ApiService } from './shared/services/api.service';
import { EncryptionService } from './shared/services/encryption.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MainLayoutComponent,
    VolunteerLayoutComponent,
    NgoLayoutComponent,
    NgIf,
    ToasterStackComponent,
    LoadingSpinnerComponent,
    ConfirmationModalComponent,
    PaymentStatusModalComponent
  ],
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'ikshana';
  showLogoutModal = false;
  
  // Payment status modal state
  showPaymentStatusModal = false;
  paymentStatus: 'success' | 'failed' | null = null;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly logoutConfirmationService: LogoutConfirmationService,
    private readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly encryptionService: EncryptionService,
  ) {}

  async ngOnInit() {
    await this.authService.init();
    this.checkAuthToken();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        window.scrollTo({ top: 0, behavior: 'auto' });
        this.checkPaymentRedirection();
      }
    });

    // Subscribe to logout modal state
    this.logoutConfirmationService.showModal$.subscribe(show => {
      this.showLogoutModal = show;
    });
  }

  private checkAuthToken() {
    const accessToken = localStorage.getItem('access_token');
    const isLoginPage = this.isLoginPage();
    const isForgotPasswordPage = this.isForgotPasswordPage();
    const isLandingPage = this.isLandingPage();

    if (!accessToken && !isLoginPage && !isForgotPasswordPage && !isLandingPage) {
      this.router.navigate(['/login']);
    }
  }

  isLoginPage() {
    return this.router.url.startsWith('/login');
  }

  isLandingPage() {
    return this.router.url.startsWith('/landing') || this.router.url === '/';
  }

  isTermsPrivacyPolicyPage() {
    return this.router.url.startsWith('/terms-privacy-policy');
  }

  isForgotPasswordPage() {
    return this.router.url.startsWith('/forgot-password');
  }

  isVolunteerPage() {
    return this.router.url.startsWith('/volunteer');
  }

  isNgoPage() {
    return this.router.url.startsWith('/ngo');
  }

  isCorporatePage() {
    return this.router.url.startsWith('/corporate');
  }

  isOnboardingPage() {
    return this.router.url.startsWith('/volunteer/onboarding') ||
           this.router.url.startsWith('/ngo/onboarding') ||
           this.router.url.startsWith('/corporate/onboarding');
  }

  isCorporateOnboardingPage() {
    return this.router.url === '/corporate/onboarding';
  }

  /**
   * Handle logout confirmation
   */
  onLogoutConfirmed(): void {
    this.logoutConfirmationService.hideLogoutModal();
    this.authService.logout().then(() => {
      this.router.navigate(['/']);
    });
  }

  /**
   * Handle logout cancellation
   */
  onLogoutCancelled(): void {
    this.logoutConfirmationService.hideLogoutModal();
  }
  
  /**
   * Check if the current URL is a PayPal redirection with payment status
   */
  private checkPaymentRedirection(): void {
    // Check if URL contains payment=success parameter
    if (this.router.url.includes('/corporate/dashboard')) {
      this.route.queryParamMap.subscribe(params => {
        const paymentStatus = params.get('payment');
        if (paymentStatus === 'success') {
          // Show success payment modal
          this.paymentStatus = 'success';
          this.showPaymentStatusModal = true;
          
          // Hide modal after 3 seconds
          setTimeout(() => {
            this.showPaymentStatusModal = false;
            this.paymentStatus = null;
          }, 3000);
          
          // Call get-onboarding-status API
          this.apiService.get<any>(`${environment.apiBaseUrl}/v1/corporate/get-onboarding-status`).subscribe({
            next: async (response: any) => {
              if (response?.data?.subscription_plan_status) {
                // Encrypt and store subscription plan in local storage
                const encrypted = await this.encryptionService.encrypt(response.data.subscription_plan_status);
                localStorage.setItem('subscription_plan', encrypted);
                
                // Navigate to dashboard without query parameters to avoid reload loop
                this.router.navigate(['/corporate/dashboard'], { replaceUrl: true });
              }
            },
            error: (err: any) => {
              console.error('Error fetching onboarding status:', err);
            }
          });
        } else if (paymentStatus === 'failed') {
          // Handle failed payment
          this.paymentStatus = 'failed';
          this.showPaymentStatusModal = true;
          
          // Hide modal after 3 seconds
          setTimeout(() => {
            this.showPaymentStatusModal = false;
            this.paymentStatus = null;
          }, 3000);
        }
      });
    }
  }
}
